
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { SwapRequest } from "@/services/myReagents";

interface EditSwapRequestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { creditOffer: number; message: string }) => void;
  isSubmitting: boolean;
  swapRequest: SwapRequest | null;
}

const EditSwapRequestDialog: React.FC<EditSwapRequestDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  isSubmitting,
  swapRequest
}) => {
  const [creditOffer, setCreditOffer] = useState<number>(swapRequest?.credit_offer || 0);
  const [message, setMessage] = useState<string>("");

  // Reset form when swapRequest changes
  React.useEffect(() => {
    if (swapRequest) {
      setCreditOffer(swapRequest.credit_offer || 0);
      setMessage("");
    }
  }, [swapRequest]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      creditOffer,
      message
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Swap Request</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {swapRequest?.credit_offer !== undefined && (
            <div className="space-y-2">
              <Label htmlFor="creditOffer">Credit Offer</Label>
              <Input
                id="creditOffer"
                type="number"
                min={0}
                value={creditOffer}
                onChange={(e) => setCreditOffer(Number(e.target.value))}
                disabled={isSubmitting}
              />
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="message">Message to Owner</Label>
            <Textarea
              id="message"
              placeholder="Explain why you're updating this request..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isSubmitting}
              rows={4}
              required
            />
            <p className="text-sm text-muted-foreground">
              The reagent owner will be notified about this change.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !message.trim()}>
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditSwapRequestDialog;
